import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Hexagon, Sun, Moon, Activity, Boxes, Wrench, LogIn, LayoutDashboard, Server } from "lucide-react";
import { Toaster } from "sonner";
import { DEV_MODE } from "../lib/ledgerApi";
import { useAuth } from "../contexts/AuthContext";
import { useBranding } from "../contexts/BrandingContext";

const LINKS = [
  { to: "/", label: "Explorer", icon: Activity, end: true },
  { to: "/blocks", label: "Blocks", icon: Boxes },
  { to: "/validators", label: "Validators", icon: Server },
];

export default function Layout() {
  const [theme, setTheme] = useState(() => {
    if (typeof window === "undefined") return "dark";
    return localStorage.getItem("neopay-theme") || "dark";
  });
  const location = useLocation();
  const { user } = useAuth();
  const { branding } = useBranding();

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("neopay-theme", theme);
  }, [theme]);

  const toggleTheme = () => setTheme((t) => (t === "dark" ? "light" : "dark"));

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <Toaster theme={theme} position="bottom-right" toastOptions={{ className: "font-mono text-xs" }} />

      <header className="sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/70">
        <div className="max-w-7xl mx-auto px-6 md:px-8 h-14 flex items-center justify-between gap-4">
          <Link to="/" data-testid="nav-brand" className="flex items-center gap-2 font-medium tracking-tight group">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt={branding.product_name} className="w-7 h-7 object-contain rounded-sm border border-foreground/40" />
            ) : (
              <span className="w-7 h-7 inline-flex items-center justify-center border border-foreground/40 rounded-sm group-hover:border-foreground transition-colors">
                <Hexagon className="w-4 h-4" strokeWidth={1.5} />
              </span>
            )}
            <span className="text-base">{branding.product_name}</span>
            <span className="hidden sm:inline-block ml-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground border-l border-border pl-2">
              Block Explorer
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1 text-sm">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-3 py-1.5 rounded-sm border transition-colors flex items-center gap-2 ${
                    isActive
                      ? "border-foreground bg-foreground text-background"
                      : "border-transparent hover:border-border text-muted-foreground hover:text-foreground"
                  }`
                }
              >
                <l.icon className="w-3.5 h-3.5" strokeWidth={1.5} />
                {l.label}
              </NavLink>
            ))}
            {DEV_MODE && (
              <NavLink
                to="/dev"
                data-testid="nav-dev"
                className={({ isActive }) =>
                  `px-3 py-1.5 rounded-sm border transition-colors flex items-center gap-2 ${
                    isActive
                      ? "border-status-tampered text-status-tampered bg-status-tampered/10"
                      : "border-transparent hover:border-status-tampered/60 text-status-tampered/80 hover:text-status-tampered"
                  }`
                }
              >
                <Wrench className="w-3.5 h-3.5" strokeWidth={1.5} />
                Dev
              </NavLink>
            )}
          </nav>

          <div className="flex items-center gap-2">
            {user ? (
              <Link
                to="/app/dashboard"
                data-testid="header-go-to-app"
                className="hidden sm:inline-flex items-center gap-1 px-3 py-1.5 rounded-sm border border-foreground bg-foreground text-background text-xs hover:opacity-90"
              >
                <LayoutDashboard className="w-3.5 h-3.5" strokeWidth={1.5} /> App
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  data-testid="header-login"
                  className="hidden sm:inline-flex items-center gap-1 px-3 py-1.5 rounded-sm border border-border text-xs hover:border-foreground"
                >
                  <LogIn className="w-3.5 h-3.5" strokeWidth={1.5} /> Login
                </Link>
                <Link
                  to="/signup"
                  data-testid="header-signup"
                  className="hidden sm:inline-flex items-center px-3 py-1.5 rounded-sm border border-foreground bg-foreground text-background text-xs hover:opacity-90"
                >
                  Get started
                </Link>
              </>
            )}
            <button
              onClick={toggleTheme}
              data-testid="theme-toggle"
              aria-label="Toggle theme"
              className="w-8 h-8 inline-flex items-center justify-center border border-border rounded-sm hover:border-foreground transition-colors"
            >
              {theme === "dark" ? <Sun className="w-4 h-4" strokeWidth={1.5} /> : <Moon className="w-4 h-4" strokeWidth={1.5} />}
            </button>
          </div>
        </div>
      </header>

      <main key={location.pathname} className="flex-1 animate-fade-in-up">
        <Outlet />
      </main>

      <footer className="border-t border-border mt-12">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-muted-foreground font-mono">
          <div>{branding.product_name} · Phase 1 — Core Payment Rail</div>
          <div className="flex items-center gap-4">
            <span>secp256k1 · SHA-256 · Merkle</span>
            <span className="opacity-50">v0.2.0</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
